import React, { memo, useContext, useMemo, useRef, useState } from 'react';
import styles from './VersionList.module.scss';
import Cross from 'assets/icons/close-button-small.svg?react';
import DocumentIcon from 'assets/icons/document-icon.svg?react';
import ArrowDown from 'assets/icons/arrow-language-selector.svg?react';
import Chevron from 'assets/icons/small-chevron-grey.svg?react';
import BinIcon from 'assets/icons/bin.svg?react';
import { IconButton } from '../../../../../components/uikit/IconButton.tsx';
import { Fetching, None, PageDesignerContext } from '../../DesignerNewPage.tsx';
import { useScopedTranslation } from '../../../../../application/localization/useScopedTranslation.ts';
import clsx from 'clsx';
import { Links } from '../../../../../application/constants/links.ts';
import {
  CmsPageQuery,
  CmsPageVersionClient,
  CmsPageVersionDto,
  CmsPageVersionQuery,
} from 'services/api/api-client.ts';
import { formatDetailedDate } from '../../../../../helpers/abas-helper.ts';
import { CustomModal } from '../../../../../components/uikit/modal/CustomModal.tsx';
import { ButtonColor } from '../../../../../components/uikit/buttons/Button.tsx';
import { useQueryClient } from '@tanstack/react-query';

type AccordionData = {
  key: string;
  ids: number[];
};

const generateRandomString = () => Math.random().toString(36).slice(2);

const autosavedGroup = 'autosavedGroup';
const manualSave = 'manualSave';

export const VersionList = memo(() => {
  const [openedAccordions, setOpenedAccordions] = useState<Set<string>>(
    new Set([]),
  );
  const [idToRemove, setIdToRemove] = useState<number | null>(null);

  const accordionData = useRef<AccordionData[]>([]);

  const pageDesignerContext = useContext(PageDesignerContext);
  const i18n = useScopedTranslation('Page.PageDesigner');
  const queryClient = useQueryClient();

  const routeParams = Links.Authorized.CustomPages.DesignerNewPage.useParams();
  const pageUrl = routeParams.url;

  const pageQuery = CmsPageQuery.useGetDetailsByUrlQuery(
    {
      url: pageUrl,
      getActiveVersion: true,
    },
    {
      throwOnError: false,
      staleTime: 3 * 60 * 1000,
    },
  );

  const pageVersionsQuery = CmsPageVersionQuery.useSearchQuery(
    {
      pageId: pageQuery.data?.id,
      limit: pageDesignerContext?.versionsPageSize,
    },
    {
      throwOnError: false,
      placeholderData: (previousData) => previousData,
    },
  );

  const getAccordionData = (ids: number[]) => {
    let key = generateRandomString();

    const currentAccordionData: AccordionData = {
      key,
      ids,
    };

    for (const el of accordionData.current) {
      // difference shouldn't be more than 1
      if (el.ids.length - ids.length > 1) {
        continue;
      }

      // full match of elements
      if (
        ids.length === el.ids.length &&
        ids.every((id, index) => id === el.ids[index])
      ) {
        currentAccordionData.key = el.key;
        break;
      }

      let skippedElems = 0;
      let j = 0;

      for (let i = 0; i < el.ids.length; i++) {
        if (el.ids[i] === ids[j]) {
          j++;
        } else {
          skippedElems++;

          if (skippedElems > 1) {
            break;
          }
        }
      }

      // match of all elements except 1
      if (skippedElems === 1) {
        currentAccordionData.key = el.key;
        break;
      }
    }

    return currentAccordionData;
  };

  // This func returns array which contains either single version or array of autosaved versions - accordion.
  // we need to identify accordions, because they have open/close state, but they don't have any ids,
  // that's why we keep accordion key and ids of its element's from last render in accordionData
  //
  // After every collected accordion, we look for suitable data in accordionData:
  // an exact match of element's ids or exact match minus one
  // if data is found, then we take key from it and React will recognize accordion from previous render = state will be saved,
  // otherwise we create a new key with a new state. In the end of the func we update data in accordionData in order to make it actual
  const pageVersions = useMemo(() => {
    const result: (
      | {
          type: typeof autosavedGroup;
          key: string;
          values: CmsPageVersionDto[];
        }
      | { type: typeof manualSave; data: CmsPageVersionDto }
    )[] = [];
    const currentAccordionDataArray: AccordionData[] = [];
    let values: CmsPageVersionDto[] = [];

    const data = pageVersionsQuery.data?.data;

    if (!data) {
      return result;
    }

    const addData = () => {
      const ids = values.map((value) => value.id);
      const currentAccordionData = getAccordionData(ids);
      currentAccordionDataArray.push(currentAccordionData);
      result.push({
        type: autosavedGroup,
        key: currentAccordionData.key,
        values,
      });
    };

    for (let i = 0; i < data.length; i++) {
      if (data[i].isPublished || data[i].id === pageQuery.data?.versionId) {
        continue;
      }

      if (data[i].isAutosaved) {
        values.push(data[i]);
      } else {
        if (values.length) {
          addData();
          values = [];
        }

        result.push({ type: manualSave, data: data[i] });
      }
    }

    if (values.length) {
      addData();
    }

    accordionData.current = currentAccordionDataArray;
    return result;
  }, [pageVersionsQuery.data?.data]);

  const handleClickAccordion = (key: string) => {
    const copy = new Set(openedAccordions);

    if (copy.has(key)) {
      copy.delete(key);
    } else {
      copy.add(key);
    }

    setOpenedAccordions(copy);
  };

  const deletePageVersion = async () => {
    try {
      if (idToRemove) {
        setIdToRemove(null);
        pageDesignerContext?.setIsLoading(Fetching);
        await CmsPageVersionClient.deleteVersion(idToRemove);

        await queryClient.invalidateQueries({
          queryKey: CmsPageVersionQuery.searchQueryKey({
            pageId: pageQuery.data?.id,
            limit: pageDesignerContext?.versionsPageSize,
          }),
        });

        if (idToRemove === pageDesignerContext?.selectedPageVersion.versionId) {
          pageDesignerContext?.setSelectedVersion({
            versionId: pageQuery.data?.versionId ?? null,
            lastUpdatedAt: pageQuery.data?.contentLastUpdatedAt ?? null,
            isActionsDisabled: true,
          });
        }
      }
    } catch (e) {
      console.error('Failed to delete version', e);
    } finally {
      pageDesignerContext?.setIsLoading(None);
    }
  };

  const selectedVersionId = pageDesignerContext?.selectedPageVersion.versionId;

  const totalCount = pageVersionsQuery?.data?.totalCount;
  const showLoadMore =
    totalCount &&
    pageDesignerContext?.versionsPageSize &&
    totalCount > pageDesignerContext?.versionsPageSize;

  const publishedVersion = pageVersionsQuery.data?.data.at(0);
  const doesPublishedExist = !!publishedVersion?.isPublished;

  // subtract active and published versions
  let numberOfVersions =
    (pageVersionsQuery.data?.data.length ?? 0) -
    1 -
    (doesPublishedExist ? 1 : 0);

  return (
    <div className={styles.wrapper}>
      <div
        className={clsx(
          styles.rowContainer,
          styles.spaceBetween,
          styles.marginBottom6,
        )}
      >
        <div className={styles.title}>{i18n.t('document_versions')}</div>
        <IconButton
          className={styles.closeButton}
          iconClassName={styles.imageContainer}
          type={'destroy'}
          onClick={pageDesignerContext?.hideVersions}
        >
          <Cross className={styles.image} />
        </IconButton>
      </div>
      <div className={styles.scrollable}>
        <div
          className={clsx(
            styles.version,
            styles.marginTop10,
            styles.spaceBetween,
            selectedVersionId === pageQuery.data?.versionId &&
              styles.selectedVersion,
          )}
          onClick={() => {
            pageDesignerContext?.setSelectedVersion({
              versionId: pageQuery.data?.versionId ?? null,
              lastUpdatedAt: pageQuery.data?.contentLastUpdatedAt ?? null,
              isActionsDisabled: true,
            });
          }}
        >
          <div className={clsx(styles.rowContainer, styles.columnGap12)}>
            <div className={clsx(styles.documentIcon)}>
              <DocumentIcon />
            </div>
            <div className={styles.columnContainer}>
              <div className={styles.text}>
                {formatDetailedDate(
                  pageQuery.data?.contentLastUpdatedAt ?? '',
                  i18n.i18n,
                )}
              </div>
              <div className={styles.createdBy}>
                {i18n.t('by')} {pageQuery.data?.lastUpdatedBy?.firstName}
              </div>
            </div>
          </div>
          <div className={styles.activeContainer}>{i18n.t('active')}</div>
        </div>
        {doesPublishedExist && (
          <div className={styles.relative}>
            <div className={styles.emptyBlock} />
            <div
              className={clsx(
                styles.version,
                styles.spaceBetween,
                selectedVersionId === publishedVersion.id &&
                  styles.selectedVersion,
              )}
              onClick={() => {
                pageDesignerContext?.setSelectedVersion({
                  versionId: publishedVersion.id,
                  lastUpdatedAt: publishedVersion.createdAt ?? null,
                  isActionsDisabled: true,
                });
              }}
            >
              <div className={clsx(styles.rowContainer, styles.columnGap12)}>
                <div className={styles.documentIcon}>
                  <DocumentIcon />
                </div>
                <div className={styles.columnContainer}>
                  <div className={styles.text}>
                    {formatDetailedDate(
                      publishedVersion.createdAt ?? '',
                      i18n.i18n,
                    )}
                  </div>
                  <div className={styles.createdBy}>
                    {i18n.t('by')} {publishedVersion.createdBy.firstName}
                  </div>
                </div>
              </div>
              <div className={styles.publishedContainer}>
                {i18n.t('published')}
              </div>
            </div>
          </div>
        )}
        {numberOfVersions > 0 && (
          <div className={clsx(styles.greyText)}>
            {i18n.t('versions_history')}
          </div>
        )}
        {pageVersions.map((version) => {
          if (version.type === autosavedGroup) {
            return (
              <div
                key={version.key}
                className={clsx(
                  styles.accordion,
                  openedAccordions.has(version.key) && styles.marginBottom0,
                )}
              >
                <div className={styles.emptyBlock} />
                <div className={clsx(styles.rowContainer, styles.columnGap12)}>
                  <div
                    className={clsx(
                      styles.chevronIcon,
                      openedAccordions.has(version.key) && styles.rotate,
                    )}
                    onClick={() => handleClickAccordion(version.key)}
                  >
                    <Chevron />
                  </div>
                  <div className={styles.autosavedText}>
                    {version.values.length} {i18n.t('autosaved_versions')}
                  </div>
                </div>
                {openedAccordions.has(version.key) &&
                  version.values.map((pageVersion) => (
                    <div key={pageVersion.id} className={styles.outer}>
                      <div className={styles.paddingBottom8}>
                        <div
                          className={clsx(
                            styles.version,
                            styles.accordionVersion,
                            styles.spaceBetween,
                            pageVersion.id === selectedVersionId &&
                              styles.selectedVersion,
                          )}
                          onClick={() => {
                            pageDesignerContext?.setSelectedVersion({
                              versionId: pageVersion.id,
                              lastUpdatedAt: pageVersion.createdAt,
                              isActionsDisabled: false,
                            });
                          }}
                        >
                          <div className={styles.columnContainer}>
                            <div className={styles.text}>
                              {formatDetailedDate(
                                pageVersion.createdAt,
                                i18n.i18n,
                              )}
                            </div>
                            <div className={styles.createdBy}>
                              {i18n.t('by')} {pageVersion.createdBy.firstName}
                            </div>
                          </div>
                          <IconButton
                            type={'destroy'}
                            iconClassName={styles.heightWidthUnset}
                            className={clsx(
                              styles.displayNone,
                              styles.marginRight5,
                            )}
                            onClick={() => setIdToRemove(pageVersion.id)}
                          >
                            <BinIcon className={styles.binIcon} />
                          </IconButton>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            );
          } else if (version.type === manualSave) {
            return (
              <div
                key={version.data.id}
                className={clsx(
                  styles.version,
                  version.data.id === selectedVersionId &&
                    styles.selectedVersion,
                )}
                onClick={() => {
                  pageDesignerContext?.setSelectedVersion({
                    versionId: version.data.id,
                    lastUpdatedAt: version.data.createdAt,
                    isActionsDisabled: false,
                  });
                }}
              >
                <div className={styles.emptyBlock} />
                <div
                  className={clsx(
                    styles.rowContainer,
                    styles.spaceBetween,
                    styles.width100,
                  )}
                >
                  <div
                    className={clsx(styles.rowContainer, styles.columnGap12)}
                  >
                    <div className={styles.documentIcon}>
                      <DocumentIcon />
                    </div>
                    <div className={styles.columnContainer}>
                      <div className={styles.text}>
                        {formatDetailedDate(version.data.createdAt, i18n.i18n)}
                      </div>
                      <div className={styles.createdBy}>
                        {i18n.t('by')} {version.data.createdBy.firstName}
                      </div>
                    </div>
                  </div>
                  <IconButton
                    type={'destroy'}
                    iconClassName={styles.heightWidthUnset}
                    className={styles.displayNone}
                    onClick={() => setIdToRemove(version.data.id)}
                  >
                    <BinIcon className={styles.binIcon} />
                  </IconButton>
                </div>
              </div>
            );
          }
        })}
        {showLoadMore && (
          <div
            className={clsx(
              styles.rowContainer,
              styles.columnGap4,
              styles.showOlder,
            )}
            onClick={pageDesignerContext?.increaseVersionPageSize}
          >
            <ArrowDown />
            <div>{i18n.t('show_older')}</div>
          </div>
        )}
      </div>
      {idToRemove && (
        <CustomModal
          isOpen={true}
          onSubmit={deletePageVersion}
          onCancel={() => setIdToRemove(null)}
          cancelButtonText={i18n.t('cancel')}
          submitButtonText={i18n.t('delete_version')}
          titleText={i18n.t('delete_version')}
          bodyText={i18n.t('delete_version_modal_body')}
          containerClassName={styles.modalBodyMinWidth480}
          textClassName={styles.lineHeight24}
          submitButtonType={ButtonColor.Warning}
        />
      )}
    </div>
  );
});
